import { useState, useMemo } from 'react';
import { Autocomplete, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { MutationError } from '@elementary/common-ui/types/mutationTypes';
import { Alert, FlexColumn, TextField } from '@elementary/common-ui/components';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import useSnackbar from 'shared/hooks/useSnackbar';
import * as api from 'shared/services/settingsApi';
import { useMicrosoftTeamsIntegration } from 'shared/hooks/integrations';
import TestMessageSection from './TestMessageSection';

type Props = {
  teamsWebhooks?: Array<{ name: string; url: string }>;
  onSuccess?: () => void;
  onReset?: () => void;
};

const MicrosoftTeamsTestSection = ({
  teamsWebhooks = [],
  onSuccess,
  onReset,
}: Props) => {
  const [selectedWebhook, setSelectedWebhook] = useState<string>('');
  const [customMessage, setCustomMessage] = useState<string>('');

  const { openSnackbar } = useSnackbar();
  const selectedEnvId = useSelectedEnv();

  const { microsoftTeamsWebhooksQuery } = useMicrosoftTeamsIntegration({
    envId: selectedEnvId,
  });

  const webhooks = useMemo(() => {
    const propWebhooks =
      teamsWebhooks?.map((w) => ({ name: w.name, url: w.url })) || [];
    const queryWebhooks =
      microsoftTeamsWebhooksQuery.data?.webhooks?.map((w) => ({
        name: w.name,
        url: w.url,
      })) || [];
    return propWebhooks.length > 0 ? propWebhooks : queryWebhooks;
  }, [teamsWebhooks, microsoftTeamsWebhooksQuery.data?.webhooks]);

  const isWebhooksLoading =
    teamsWebhooks?.length > 0 ? false : microsoftTeamsWebhooksQuery.isLoading;

  const webhooksError =
    teamsWebhooks?.length > 0 ? null : microsoftTeamsWebhooksQuery.error;

  const defaultMessage =
    'This is a test message from Elementary Data to verify your Microsoft Teams integration is working correctly.';

  const testMutation = useMutation(
    async (data: { webhook_name: string; message?: string }) => {
      // Find the webhook URL from currently shown webhooks
      const selectedWebhookData = webhooks.find(
        (w) => w.name === data.webhook_name,
      );
      if (!selectedWebhookData) {
        return {
          success: false,
          message: `Webhook "${data.webhook_name}" not found in current configuration`,
        };
      }

      const requestData = {
        webhook_name: data.webhook_name,
        webhook_url: selectedWebhookData.url,
        message: data.message,
      };

      const response = await api.sendTeamsTestMessage(requestData);
      return response;
    },
    {
      onSuccess: (response) => {
        if (response.success) {
          openSnackbar({
            message: 'Test message sent successfully to Teams!',
            variant: 'success',
          });
          onSuccess?.();
        } else {
          openSnackbar({
            message: response.message || 'Failed to send test message to Teams',
            variant: 'error',
          });
          onReset?.();
        }
      },
      onError: (error: MutationError) => {
        openSnackbar({
          message:
            error?.message ||
            error?.response?.data?.message ||
            'Failed to send test message to Teams',
          variant: 'error',
        });
      },
    },
  );

  const handleSendTest = () => {
    if (!selectedWebhook) return;

    const message = customMessage.trim() || defaultMessage;
    testMutation.mutate({
      webhook_name: selectedWebhook,
      message,
    });
  };

  const errorMessage = webhooksError
    ? 'Failed to load available webhooks. Please try again.'
    : null;

  const autocompleteSelectedVal =
    webhooks.find((w) => w.name === selectedWebhook) || null;

  return (
    <FlexColumn sx={{ gap: 3 }}>
      <Typography>
        Send a test message to verify your Microsoft Teams integration is
        working correctly.
      </Typography>

      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

      <Autocomplete
        options={webhooks}
        value={autocompleteSelectedVal}
        onChange={(_, newValue) => setSelectedWebhook(newValue?.name || '')}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, value) => option.name === value.name}
        loading={isWebhooksLoading}
        disabled={testMutation.isLoading}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Select Teams webhook"
            placeholder="e.g., my-webhook"
            fullWidth
            size="large"
          />
        )}
      />

      <TestMessageSection
        customMessage={customMessage}
        onCustomMessageChange={setCustomMessage}
        defaultMessage={defaultMessage}
        onSendTest={handleSendTest}
        isLoading={testMutation.isLoading}
        disabled={!selectedWebhook}
      />
    </FlexColumn>
  );
};

export default MicrosoftTeamsTestSection;
